import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    TablePagination,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
    getFirestore,
    collection,
    getDocs,
    updateDoc,
    deleteDoc,
    doc,
    setDoc,
} from "firebase/firestore";

import {
    getAuth,
    createUserWithEmailAndPassword,
} from "firebase/auth";

import Header from "../panel/Header";

const db = getFirestore();
const auth = getAuth();

function Usermgmt() {
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [form, setForm] = useState({
        email: "",
        name: "",
        password: "",
        role: "",
    });
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Fetch users from Firestore
    const fetchUsers = async () => {
        const snapshot = await getDocs(collection(db, "users"));
        setUsers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Handle input change
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Add or Update user
    // Save User
    const handleSave = async () => {
        if (editUser) {
            // Update Firestore user
            const userRef = doc(db, "users", editUser.id);
            await updateDoc(userRef, {
                name: form.name,
                email: form.email,
                role: form.role,
            });
        } else {
            // Create new user in Firebase Auth
            const userCred = await createUserWithEmailAndPassword(
                auth,
                form.email,
                form.password
            );
            const uid = userCred.user.uid;

            // Save in Firestore
            await setDoc(doc(db, "users", uid), {
                uid,
                name: form.name,
                email: form.email,
                role: form.role,
            });
        }

        setForm({ email: "", name: "", password: "", role: "" });
        setEditUser(null);
        setOpen(false);
        fetchUsers();
    };


    // Delete user (Firestore only)
    const handleDelete = async (user) => {
        await deleteDoc(doc(db, "users", user.id));
        fetchUsers();
    };

    // Filter users by search
    const filteredUsers = users.filter(
        (u) =>
            u.name?.toLowerCase().includes(search.toLowerCase()) ||
            u.email?.toLowerCase().includes(search.toLowerCase()) ||
            u.role?.toLowerCase().includes(search.toLowerCase())
    );

    // Pagination slice
    const paginatedUsers = filteredUsers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box p={3}>
            <Header />
            <Typography variant="h4" mb={2}>
                User Management
            </Typography>

            {/* Search + Add button */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <TextField
                    label="Search by name, email or role"
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: "300px" }}
                />
                <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                    Add User
                </Button>
            </Box>

            <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Sr No</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>UID</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedUsers.map((user, srno = 1) => (
                            <TableRow key={user.id}>
                                <TableCell>{srno + 1}</TableCell>
                                <TableCell>{user.name}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{user.role}</TableCell>
                                <TableCell>{user.uid}</TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        color="primary"
                                        onClick={() => {
                                            setEditUser(user);
                                            setForm(user);
                                            setOpen(true);
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => handleDelete(user)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={filteredUsers.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* Add/Edit User Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
                <DialogTitle>{editUser ? "Edit User" : "Add User"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="dense"
                        size="small"
                        label="Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label="Email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                    />
                    {!editUser && (
                        <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label="Password"
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                        />
                    )}
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Role</InputLabel>
                        <Select
                            size="small"
                            name="role"
                            value={form.role}
                            onChange={handleChange}
                            label="Role"
                        >
                            <MenuItem value="Superadmin">Superadmin</MenuItem>
                            <MenuItem value="Manager">Manager</MenuItem>
                            <MenuItem value="Templeadmin">Templeadmin</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editUser ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Usermgmt;
